/**
 * Baydoun Footwear — Theme JavaScript
 */
(function () {
  'use strict';

  function initHeader() {
    const wrapper = document.querySelector('.baydoun-header-wrapper');
    if (!wrapper) return;

    const isTransparent = wrapper.dataset.transparent === 'true';

    function updateHeader() {
      if (!isTransparent) return;
      wrapper.classList.toggle('is-scrolled', window.scrollY > 60);
    }

    window.addEventListener('scroll', updateHeader, { passive: true });
    updateHeader();

    const drawer = document.querySelector('.baydoun-drawer');
    const openBtn = document.querySelector('[data-baydoun-drawer-open]');
    const closeTriggers = document.querySelectorAll('[data-baydoun-drawer-close]');

    function openDrawer() {
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('baydoun-nav-open');
      document.body.style.overflow = 'hidden';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      const firstLink = drawer.querySelector('.baydoun-drawer__menu a');
      if (firstLink) firstLink.focus({ preventScroll: true });
    }

    function closeDrawer() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('baydoun-nav-open');
      document.body.style.overflow = '';
      if (openBtn) {
        openBtn.setAttribute('aria-expanded', 'false');
        openBtn.focus({ preventScroll: true });
      }
    }

    openBtn?.addEventListener('click', openDrawer);
    closeTriggers.forEach((el) => el.addEventListener('click', closeDrawer));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer();
    });
  }

  function initFadeIn() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.baydoun-fade-in').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const elements = document.querySelectorAll('.baydoun-fade-in');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach((el) => observer.observe(el));
  }

  function initStickyATC() {
    const stickyBar = document.querySelector('.baydoun-sticky-atc');
    const productForm = document.querySelector('.product-form');
    const submitBtn = productForm?.querySelector('[type="submit"]');
    if (!stickyBar || !submitBtn) return;

    const stickyBtn = stickyBar.querySelector('[data-sticky-atc]');
    stickyBtn?.addEventListener('click', () => submitBtn.click());

    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(submitBtn);
  }

  function initScrollDrag(root) {
    (root || document).querySelectorAll('.baydoun-scroll__track:not([data-drag-ready])').forEach((track) => {
      track.setAttribute('data-drag-ready', 'true');
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;

      track.addEventListener('mousedown', (e) => {
        isDown = true;
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
        track.style.cursor = 'grabbing';
      });
      track.addEventListener('mouseleave', () => {
        isDown = false;
        track.style.cursor = 'grab';
      });
      track.addEventListener('mouseup', () => {
        isDown = false;
        track.style.cursor = 'grab';
      });
      track.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - track.offsetLeft;
        track.scrollLeft = scrollLeft - (x - startX) * 1.5;
      });
      track.style.cursor = 'grab';
    });
  }

  function bindCarousel(carousel) {
    if (carousel.getAttribute('data-baydoun-carousel-ready') === 'true') return;
    carousel.setAttribute('data-baydoun-carousel-ready', 'true');

    const track = carousel.querySelector('[data-baydoun-carousel-track]');
    const prev = carousel.querySelector('[data-baydoun-carousel-prev]');
    const next = carousel.querySelector('[data-baydoun-carousel-next]');
    if (!track) return;

    const gap = () => {
      const styles = window.getComputedStyle(track);
      return parseFloat(styles.columnGap || styles.gap) || 16;
    };

    const step = () => {
      const item = track.querySelector('.baydoun-scroll__item');
      return item ? item.getBoundingClientRect().width + gap() : track.clientWidth * 0.8;
    };

    const syncButtons = () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 1;
      if (prev) prev.disabled = track.scrollLeft <= 0;
      if (next) next.disabled = track.scrollLeft >= maxScroll;
    };

    prev?.addEventListener('click', () => track.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step() * 2, behavior: 'smooth' }));
    track.addEventListener('scroll', syncButtons, { passive: true });
    window.addEventListener('resize', syncButtons, { passive: true });
    syncButtons();

    const autoplayEnabled =
      carousel.getAttribute('data-baydoun-carousel-autoplay') === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!autoplayEnabled) return;

    const autoplayDelay = 4200;
    let autoplayTimer = null;
    let resumeTimer = null;
    let carouselIsVisible = true;
    let interactionPaused = false;

    const stopAutoplay = () => {
      if (autoplayTimer) window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const advanceCarousel = () => {
      if (!carousel.isConnected) {
        stopAutoplay();
        return;
      }

      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      if (maxScroll <= 1) return;

      if (track.scrollLeft >= maxScroll - step() * 0.35) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      }
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!carouselIsVisible || interactionPaused || document.hidden || !carousel.isConnected) return;
      autoplayTimer = window.setInterval(advanceCarousel, autoplayDelay);
    };

    const pauseForInteraction = () => {
      interactionPaused = true;
      if (resumeTimer) window.clearTimeout(resumeTimer);
      stopAutoplay();
    };

    const resumeAfterInteraction = () => {
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        interactionPaused = false;
        startAutoplay();
      }, 2400);
    };

    carousel.addEventListener('mouseenter', pauseForInteraction);
    carousel.addEventListener('mouseleave', resumeAfterInteraction);
    carousel.addEventListener('focusin', pauseForInteraction);
    carousel.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!carousel.contains(document.activeElement)) resumeAfterInteraction();
      }, 0);
    });
    carousel.addEventListener('touchstart', pauseForInteraction, { passive: true });
    carousel.addEventListener('touchend', resumeAfterInteraction, { passive: true });
    carousel.addEventListener('touchcancel', resumeAfterInteraction, { passive: true });

    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          carouselIsVisible = entry.isIntersecting;
          if (carouselIsVisible) startAutoplay();
          else stopAutoplay();
        },
        { threshold: 0.2 }
      );
      visibilityObserver.observe(carousel);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    startAutoplay();
  }

  function initCarousels(root) {
    (root || document).querySelectorAll('[data-baydoun-carousel]').forEach(bindCarousel);
    initScrollDrag(root);
  }

  function watchDynamicCarousels() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches?.('[data-baydoun-carousel]')) bindCarousel(node);
          node.querySelectorAll?.('[data-baydoun-carousel]').forEach(bindCarousel);
          if (node.matches?.('.baydoun-scroll__track') || node.querySelector?.('.baydoun-scroll__track')) {
            initScrollDrag(node);
          }
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initWhatsAppFab() {
    const fab = document.querySelector('.bd-whatsapp-fab');
    if (!fab || fab.getAttribute('data-wa-ready') === 'true') return;
    fab.setAttribute('data-wa-ready', 'true');

    const callout = document.querySelector('.bd-whatsapp-callout');
    const source = document.getElementById('BaydounWhatsAppProduct');
    const isProduct = fab.hasAttribute('data-wa-product');

    let productUrl = '';
    if (source) {
      try {
        const catalog = JSON.parse(source.textContent);
        productUrl = catalog && catalog.url ? String(catalog.url).trim() : '';
      } catch (error) {
        productUrl = '';
      }
    }

    const match = String(fab.getAttribute('href') || '').match(/wa\.me\/(\d+)/i);
    const number = match ? match[1] : '';

    function buildHref() {
      if (!number) return fab.getAttribute('href') || 'https://wa.me/96176670097';
      if (!isProduct || !productUrl) return 'https://wa.me/' + number;
      const message = 'Hello, I have a question about this product.\n\n' + productUrl;
      return 'https://wa.me/' + number + '?text=' + encodeURIComponent(message);
    }

    function hideCallout() {
      if (!callout) return;
      callout.classList.remove('is-visible', 'is-nudge');
      callout.setAttribute('hidden', '');
      callout.setAttribute('aria-hidden', 'true');
    }

    function openWhatsApp(event) {
      if (event) event.preventDefault();
      hideCallout();
      const href = buildHref();
      fab.setAttribute('href', href);
      if (callout) callout.setAttribute('href', href);
      window.open(href, '_blank', 'noopener,noreferrer');
    }

    if (!isProduct) return;

    const href = buildHref();
    fab.setAttribute('href', href);
    fab.addEventListener('click', openWhatsApp);

    if (!callout) return;

    callout.setAttribute('href', href);
    callout.addEventListener('click', openWhatsApp);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(() => {
      if (callout.hasAttribute('data-wa-shown')) return;
      callout.setAttribute('data-wa-shown', 'true');
      callout.removeAttribute('hidden');
      callout.setAttribute('aria-hidden', 'false');
      if (reduceMotion) {
        callout.classList.add('is-visible');
      } else {
        callout.classList.add('is-visible');
        window.setTimeout(() => callout.classList.add('is-nudge'), 700);
        window.setTimeout(() => callout.classList.remove('is-nudge'), 1300);
      }
      window.setTimeout(hideCallout, 7000);
    }, 1000);
  }

  function boot() {
    initHeader();
    initFadeIn();
    initStickyATC();
    initCarousels();
    watchDynamicCarousels();
    initWhatsAppFab();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
